<template>
    <div class="intro box mt-5" id="box-form" v-if="loaded">
        <div class="flex flex-col sm:flex-row items-center p-5 border-b border-slate-200/60 dark:border-darkmode-400">
            <h2 class="font-medium text-base mr-auto">Instrumenten-Kiste bearbeiten / anlegen</h2>
        </div>
        <div class="p-5">

            <FormErrorBox />

            <div class="form-inline">
                <label for="nr" class="form-label sm:w-40">Name</label>
                <input
                    id="nr"
                    type="text"
                    class="form-control"
                    v-model="form.name"
                >
            </div>

            <div class="form-inline mt-2" v-if="form.id > 0">
                <label for="instruments" class="form-label sm:w-40">Instrument hinzufügen</label>
                <VueMultiselect
                    id="instruments"
                    :model-value="selectedInstruments"
                    :options="instruments"
                    :searchable="true"
                    :close-on-select="true"
                    :clear-on-select="false"
                    :allow-empty="false"
                    @update:model-value="onInstrumentSelected"
                    label="name"
                    placeholder="Bitte Instrument wählen"
                    track-by="id"
                    style="width: 80%;font-size: 14px;"
                />
            </div>

            <div class="mt-4" v-if="form.id > 0">
                <h3 class="text-2xl dark:text-white">Instrumente</h3>
                <table class="table table-bordered table-condensed table-hover table-striped">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Seriennummer</th>
                            <th width="60"></th>
                        </tr>
                    </thead>
                    <tbody>
                    <tr v-if="!form.instruments || form.instruments.length < 1">
                        <td colspan="3">es liegen keine Daten vor</td>
                    </tr>
                    <tr v-for="instrument in form.instruments" :key="instrument.id">
                        <td>{{ instrument.name }}</td>
                        <td>{{ instrument.serial_number }}</td>
                        <td>
                            <div class="flex justify-center items-center">
                                <a
                                    class="btn btn-xs btn-success mr-3 edit"
                                    href="javascript:;"
                                    title="bearbeiten"
                                    @click.prevent="onEdit(instrument)"
                                >
                                    <i data-lucide="wrench" class="w-4 h-4 mr-1 edit"></i>
                                </a>
                                <a
                                    class="btn btn-xs btn-warning mr-3 trash"
                                    href="javascript:;"
                                    title="Aus dieser Kiste entfernen"
                                    @click.prevent="onDelete(instrument)"
                                >
                                    <i data-lucide="trash" class="w-4 h-4 mr-1 trash"></i>
                                </a>
                            </div>
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>

            <div class="px-5 pt-8 pb-8 text-center">
                <button
                    type="button"
                    @click="this.$emit('close')"
                    class="btn btn-outline-warning w-24 mr-1"
                >
                    abbrechen
                </button>
                <button
                    type="button"
                    class="btn btn-success w-24"
                    @click="onSave"
                >
                    speichern
                </button>
            </div>
        </div>
    </div>


    <!-- BEGIN: Edit instrument Modal -->
    <Modal
        v-if="editInstrumentModal"
        :show="editInstrumentModal"
        :backdrop="'static'"
        :size="'modal-xxl'"
        @hidden="onEditEnd()"
    >
        <ModalBody class="p-0">
            <EditInstrumentForm
                :instrument="editInstrument"
                @save="onEditEnd"
                @close="onEditEnd"
                @hidden="onEditEnd"
                @update="update"
            />
        </ModalBody>
    </Modal>
    <!-- END: Edit instrument Modal -->

    <!-- BEGIN: Delete Confirmation Modal -->
    <Modal
        :backdrop="'static'"
        :show="deleteConfirmationModal"
        @hidden="onDeleteEnd()"
    >
        <ModalBody class="p-0">
            <div class="p-5 text-center">
                <XCircleIcon class="w-16 h-16 text-danger mx-auto mt-3"/>
                <div class="text-3xl mt-5">Instrument aus dieser Kiste entfernen</div>
                <div class="text-slate-500 mt-2">
                    Wollen Sie dieses Instrument wirklich aus dieser Kiste entfernen?
                </div>
            </div>
            <div class="px-5 pb-8 text-center">
                <button
                    type="button"
                    @click="onDeleteEnd()"
                    class="btn btn-outline-secondary w-24 mr-1"
                >
                    abbrechen
                </button>
                <button
                    type="button"
                    class="btn btn-danger w-24"
                    @click="doDelete()"
                >
                    ja
                </button>
            </div>
        </ModalBody>
    </Modal>
    <!-- END: Delete Confirmation Modal -->

</template>

<script>
import axios from "axios";
import FormErrorBox from "./FormErrorBox.vue";
import authHeader from "../services/auth-header";
import VueMultiselect from 'vue-multiselect';
import EditInstrumentForm from '../partials/EditInstrumentForm';
import {createIcons, icons} from "lucide";

export default {
    name: "EditBoxForm",
    components: {
        FormErrorBox,
        VueMultiselect,
        EditInstrumentForm
    },

    props: {
        box: {
            type: Object,
            default: {},
            required: true,
        },
    },

    data() {
        return {
            loaded                  : false,
            instruments             : [],
            selectedInstruments     : [],
            editInstrumentModal     : false,
            editInstrument          : null,
            editRowIndex            : null,
            deleteConfirmationModal  : false,
            deleteModel             : null,
            form : {
                name: '',
                instruments: [],
            },
        }
    },

    mounted() {
        this.form = JSON.parse(JSON.stringify(this.box));
        this.loaded = true;

        axios
            .get(this.$store.state.config.apiUrl + '/instrumentForBoxes', { headers: authHeader() })
            .then(response => {
                this.instruments = response.data.data;
                this.showIcons();
            });
    },

    methods: {

        onDeleteEnd() {
            this.deleteConfirmationModal = false;
            this.deleteModel = null;
            this.enableScrolling();
            this.showIcons();
        },

        doDelete() {
            this.selectedInstruments = this.selectedInstruments.filter(i => i.id !== this.deleteModel.id);
            this.form.instruments = this.form.instruments.filter(i => i.id !== this.deleteModel.id);

            axios
                .post(this.$store.state.config.apiUrl + '/removeInstrumentFromBox', {id: this.deleteModel.id}, { headers: authHeader() })
                .then(response => {
                    this.deleteConfirmationModal = false;
                    this.deleteModel = null;

                    this.showNotification(true, 'Entfernen', 'Das Instrument wurde entfernt');
                })
                .catch(error => {
                    this.showNotification(true, 'Entfernen', error.response.data.message);
                });
        },

        onDelete(instrument) {
            this.deleteModel = instrument;
            this.deleteConfirmationModal = true;
        },

        onEdit(instrument) {
            this.editInstrument = instrument;
            this.editInstrumentModal = true;
        },

        onEditEnd(updatedModel) {
            this.editInstrumentModal = false;
            this.editInstrument = null;
        },

        onInstrumentSelected(instrument) {
            this.form.instruments.push(instrument);
            this.instruments = this.instruments.filter(i => i.id !== instrument.id);

            this.showIcons();
        },

        showIcons() {
            setTimeout(() => {
                createIcons({
                    icons,
                    color: '#fff',
                    "stroke-width": 2,
                    nameAttr: "data-lucide",
                });
            }, 500);
        },

        onSave() {
            $('#producer-form .text-danger').html('');
            $('#producer-form input.border-danger').removeClass('border-danger');

            this.form.instruments.push(...JSON.parse(JSON.stringify(this.selectedInstruments)));

            if (this.form.id == null || this.form.id < 1) {
                this.create();
            } else {
                this.update();
            }
        },

        create() {
            axios
                .post(this.$store.state.config.apiUrl + '/boxes', this.form, { headers: authHeader() })
                .then(response => {
                    this.$emit('save');

                    this.showNotification(true, 'Speichern', 'Die Daten wurden gespeichert');
                })
                .catch(response => {
                    this.showFormErrors(response.response.data.errors);
                });
        },

        update() {
            axios
                .put(this.$store.state.config.apiUrl + '/boxes/' + this.form.id, this.form, { headers: authHeader() })
                .then(response => {
                    this.$emit('save');

                    this.showNotification(true, 'Speichern', 'Die Daten wurden gespeichert');
                })
                .catch(response => {
                    this.showFormErrors(response.response.data.errors);
                });
        },

        showFormErrors(error) {
            _.each(error, (message, key) => {
                $('#box-form #' + key).addClass('border-danger');
                let box = $('#producer-form .text-danger');
                if (box != null) {
                    box.append('<p>' + message[0] + '</p>');
                }
            });
        }
    }
}
</script>
